import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const gear = [
  {
    n: "01",
    cat: "Cameras",
    items: ["Sony A7 IV", "Insta 360X", "DJI Action", "DJI 04 Air Unit"],
    why: "A full-frame body for cinematic latitude, plus specialty capture bodies for action, 360°, and micro-aerial work. Every scene, covered from the right angle.",
  },
  {
    n: "02",
    cat: "Aerial Fleet",
    items: ["Mini 4 Pro — sub 250g", "Avata 2 FPV", "DollyFly — sub 250g FPV", "Darkstar Micro — sub 250g FPV"],
    why: "Four aircraft covering wide narrative pulls, dynamic FPV reveals, and regulation-friendly indoor and event fly-throughs. Nothing over 250g when the venue cannot permit it.",
  },
  {
    n: "03",
    cat: "Stabilization",
    items: ["DJI RS5", "Monopod", "Tripod", "In-Camera Stabilization", "GyroFlow", "Post-Production Digital Stabilization"],
    why: "Six layers of stabilization — mechanical, optical and digital — engineered to make every move feel deliberate. The motion reads as directorial choice, not accident.",
  },
  {
    n: "04",
    cat: "Lighting",
    items: ["Fully Mobile Studio", "C-Stands", "Soft Boxes", "Boom Stands", "Dampening Blankets", "Lighting Modifiers", "Reflectors"],
    why: "A portable, on-location studio built anywhere within an hour. Interviews, portraits and product tables shaped to broadcast standard, wherever the story needs it.",
  },
  {
    n: "05",
    cat: "Audio",
    items: ["Professional-Grade Sound Recording"],
    why: "Lavaliers, boom, monitored inputs. Your vows, your speeches, your interview answers — captured with the fidelity they deserve.",
  },
  {
    n: "06",
    cat: "Post-Production",
    items: ["DaVinci Resolve", "Photoshop", "AI Augmentation"],
    why: "Colour grading at broadcast standard, image reconstruction where the frame demanded more, and AI-assisted retouching held to a photographer's eye — not a preset.",
  },
];

const stack = [
  { label: "Galleries", value: "ShootProof — private, hi-res delivery" },
  { label: "Video Review", value: "Frame.io — timecoded notes" },
  { label: "Final Delivery", value: "Dropbox · Google Drive" },
  { label: "Invoicing", value: "Wave Accounting — clean, secure" },
];

export default function Studio() {
  return (
    <main className="pt-24 md:pt-28" data-testid="page-studio">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="h-eyebrow mb-6">§ The Studio & Gear</div>
        <h1 className="h-display text-5xl md:text-7xl leading-[0.95] max-w-4xl">
          Every tool exists for one reason —<br/>
          <span className="italic font-serif-editorial text-[var(--gold)] font-light">your final frame.</span>
        </h1>
        <p className="font-serif-editorial text-lg md:text-xl leading-relaxed text-[var(--ink)]/80 mt-10 max-w-2xl">
          Gear lists are boring. Outcomes are not. Below is what we carry &mdash; and, more importantly, why it matters to the piece of work you&rsquo;re commissioning.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-20 md:mt-24 space-y-12 md:space-y-16">
        {gear.map((g, i) => (
          <motion.article
            key={g.n}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border-t border-white/5 pt-8 md:pt-10"
            data-testid={`gear-${g.n}`}
          >
            <div className="md:col-span-2">
              <div className="numeral text-6xl md:text-8xl leading-none">{g.n}</div>
            </div>
            <div className="md:col-span-4">
              <div className="h-eyebrow mb-3">{g.cat}</div>
              <ul className="space-y-1.5">
                {g.items.map((it) => (
                  <li key={it} className="h-display text-xl md:text-2xl leading-tight text-[var(--ink)]">{it}</li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-6">
              <p className="font-serif-editorial text-lg md:text-xl leading-relaxed text-[var(--ink)]/80">
                {g.why}
              </p>
            </div>
          </motion.article>
        ))}
      </section>

      {/* Tech stack for trust */}
      <section className="bg-[var(--bg)] mt-20 md:mt-28 py-20 md:py-24 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="h-eyebrow mb-6">§ Deliverables Stack</div>
          <h2 className="h-display text-4xl md:text-6xl leading-[0.95] max-w-3xl">
            Frictionless delivery,<br/>
            <span className="italic font-serif-editorial text-[var(--gold)] font-light">by design.</span>
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
            {stack.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="flex items-baseline justify-between border-b border-white/5 pb-6"
                data-testid={`stack-${s.label.toLowerCase().replace(/\W+/g, '-')}`}
              >
                <div className="h-eyebrow text-[var(--ink-muted)]">{s.label}</div>
                <div className="font-serif-editorial italic text-lg md:text-xl text-[var(--gold)] text-right">{s.value}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-24 border-t border-white/5 text-center">
        <Link
          to="/contact"
          data-testid="studio-cta"
          className="inline-flex items-center gap-3 bg-[var(--royal)] hover:bg-[var(--royal-soft)] text-white px-10 py-5 font-sans-display uppercase tracking-[0.3em] text-[11px] transition-colors"
        >
          Commission a Project →
        </Link>
      </section>
    </main>
  );
}
